import * as actions from './core/actions';
import { compensateScroll, measureLayout, projectRows, projectLeadRows, retainedSlice } from './core/geometry';
import { createState } from './core/initial';
import { reducer } from './core/reducer';
import { deriveIntent, leadEligible, liveEligible, presentation } from './core/selectors';
import { TimeListDriver } from './driver';
import { computed, signal } from '@preact/signals-core';
export class SymbolTrackedTimeList {
    options;
    #state;
    #driver;
    #timers = new Map();
    #disposables = new DisposableStack();
    #view = null;
    #dispatching = false;
    #queuedActions = [];
    state;
    rows;
    leadRows;
    projection;
    leadProjection;
    displayState;
    atLiveTop;
    constructor(options) {
        this.options = options;
        for (const name of [
            'pageSize',
            'retainedWindowRows',
            'overscanRows',
            'rowHeight',
            'bufferHeight',
            'liveInterval',
            'leadInterval',
        ]) {
            if (!Number.isFinite(options.config[name]) || options.config[name] <= 0) {
                throw new RangeError(`${name} must be positive and finite`);
            }
        }
        for (const name of ['separatorHeight', 'pinnedTolerance']) {
            if (!Number.isFinite(options.config[name]) || options.config[name] < 0) {
                throw new RangeError(`${name} must be nonnegative and finite`);
            }
        }
        for (const name of ['pageSize', 'retainedWindowRows', 'overscanRows']) {
            if (!Number.isInteger(options.config[name]))
                throw new RangeError(`${name} must be an integer`);
        }
        this.#validateSource(options.source);
        this.#state = signal(createState(crypto.randomUUID(), options.source));
        this.state = computed(() => this.#state.value);
        this.rows = computed(() => this.#state.value.rows);
        this.leadRows = computed(() => this.#state.value.lead.rows);
        this.projection = computed(() => projectRows(this.#state.value, options.adapter));
        this.leadProjection = computed(() => projectLeadRows(this.#state.value, options.adapter));
        this.displayState = computed(() => presentation(this.#state.value));
        this.atLiveTop = computed(() => liveEligible(this.#state.value, options.config));
        this.#driver = new TimeListDriver(options.adapter, options.transport, (action) => this.#dispatch(action), () => options.scheduler.now());
        this.#disposables.defer(() => {
            for (const timer of this.#timers.values())
                timer.cancel();
            this.#timers.clear();
            this.#view = null;
        });
    }
    beginSource(source) {
        this.#validateSource(source);
        this.#dispatch(actions.beginSource(source));
    }
    activateSource(source, symbols) {
        this.#validateSource(source);
        this.#validateSymbols(source, symbols);
        this.#dispatch(actions.activateSource(source, symbols, this.options.scheduler.now()));
    }
    membershipReset() {
        this.#dispatch(actions.membershipReset());
    }
    activateSymbols(symbols) {
        this.#validateSymbols(this.#state.peek().source, symbols);
        this.#dispatch(actions.activateSymbols(symbols, this.options.scheduler.now()));
    }
    publishSymbols(symbols) {
        this.#validateSymbols(this.#state.peek().source, symbols);
        this.#dispatch(actions.publishSymbols(symbols, this.options.scheduler.now()));
    }
    updateSymbols(added, removed) {
        const symbols = new Set(this.#state.peek().activeSymbols);
        for (const symbol of removed)
            symbols.delete(symbol);
        for (const symbol of added)
            symbols.add(symbol);
        this.publishSymbols([...symbols]);
    }
    setRetention(retention) {
        this.#validateRetention(retention);
        this.#dispatch(actions.setRetention(retention, this.options.scheduler.now()));
    }
    measure(measurement, cause = 'scroll') {
        this.#dispatch(actions.measure(measurement, cause));
    }
    retry() {
        this.#dispatch(actions.retry());
    }
    requestUp() {
        this.#dispatch(actions.manualUp());
    }
    resetCurrentSource() {
        this.#dispatch(actions.resetCurrentSource());
    }
    /** The host must supply a new live query when leaving historical mode. */
    returnToTop() {
        const state = this.#state.peek();
        if (state.source.mode === 'historical')
            return 'activate-live-source';
        if (state.caughtUp || state.liveCatchUp === 'active' || state.liveTopRecovery) {
            this.#view?.scrollTo(0);
            this.measure({ ...state.measurement, scrollTop: 0 });
            return 'scrolled';
        }
        this.resetCurrentSource();
        return 'reset';
    }
    connectView(view) {
        if (this.#state.peek().disposed)
            return () => undefined;
        if (this.#view)
            throw new Error('A time list can have only one mounted view');
        this.#view = view;
        this.measure(view.read());
        return () => {
            if (this.#view === view) {
                this.#view = null;
                this.measure({ ...this.#state.peek().measurement, viewportHeight: 0 });
            }
        };
    }
    [Symbol.dispose]() {
        if (this.#state.peek().disposed)
            return;
        this.#reduce(actions.dispose());
        this.#driver.sync(this.#state.peek());
        this.#disposables.dispose();
    }
    #validateRetention(retention) {
        if (!Number.isFinite(retention) || retention < 0) {
            throw new RangeError('Retention must be nonnegative and finite');
        }
    }
    #validateSource(source) {
        this.#validateRetention(source.retention);
    }
    #validateSymbols(source, symbols) {
        if (source.selectionKind === 'single-symbol' && new Set(symbols).size > 1) {
            throw new RangeError('Single-symbol sources accept at most one symbol');
        }
    }
    #reduce(action) {
        this.#state.value = reducer(this.#state.peek(), action, this.options.adapter, this.options.config);
    }
    #layout() {
        return measureLayout(projectRows(this.#state.peek(), this.options.adapter), this.#state.peek().measurement, this.options.config, liveEligible(this.#state.peek(), this.options.config));
    }
    #dispatch(action) {
        if (this.#state.peek().disposed)
            return;
        if (this.#dispatching) {
            this.#queuedActions.push(action);
            return;
        }
        this.#dispatching = true;
        try {
            this.#run(action);
            while (this.#queuedActions.length > 0)
                this.#run(this.#queuedActions.shift());
        }
        finally {
            this.#dispatching = false;
        }
    }
    #run(action) {
        if (this.#state.peek().disposed)
            return;
        const old = this.#state.peek();
        if (this.#view)
            this.#reduce(actions.measure(this.#view.read()));
        // ResizeObserver runs after browser layout: the last committed heights are the old geometry.
        // Use the current scroll position so a simultaneous user scroll is never undone.
        const resizing = action.type === 'measure' && action.cause === 'resize';
        // Leaving pinned recovery restores a placeholder. Preserve the user's new reading offset
        // against the old projection before that insertion, just as for a resize.
        const releasingLiveTop = old.liveTopRecovery &&
            action.type === 'measure' &&
            action.measurement.scrollTop > this.options.config.pinnedTolerance;
        const before = resizing || releasingLiveTop
            ? measureLayout(projectRows(old, this.options.adapter), {
                ...old.measurement,
                scrollTop: action.measurement.scrollTop,
            }, this.options.config, liveEligible({ ...old, measurement: { ...old.measurement, scrollTop: action.measurement.scrollTop } }, this.options.config))
            : this.#layout();
        this.#reduce(action);
        if (this.#state.peek() === old)
            return;
        // Invalidation precedes abort, and no replacement transport starts until geometry is final.
        if (this.#state.peek().fetch.status !== 'in-flight')
            this.#driver.sync(this.#state.peek());
        if (!this.#state.peek().disposed) {
            const navigation = action.type === 'beginSource' ||
                action.type === 'activateSource' ||
                action.type === 'resetCurrentSource';
            if (navigation)
                this.#view?.scrollTo(0);
            this.#reconcileLayout(before, (action.type !== 'measure' || resizing || releasingLiveTop) && !navigation);
            const layout = this.#layout();
            const ordinaryKeys = new Set(this.#state.peek().rows.map((row) => this.options.adapter.keyOf(row)));
            // Anchor against all displayed rows, but slice indexes address only the paged collection.
            const slice = retainedSlice({ ...layout, rows: layout.rows.filter((row) => ordinaryKeys.has(row.key)) }, this.options.config);
            if (slice) {
                const beforeTrim = this.#layout();
                this.#reduce(actions.trim(slice.start, slice.end, this.options.scheduler.now()));
                this.#reconcileLayout(beforeTrim, true);
            }
            const beforeBoundary = this.#layout();
            const beforeRound = this.#state.peek();
            this.#reduce(actions.reconcileRound(this.options.scheduler.now()));
            if (this.#state.peek() !== beforeRound)
                this.#reconcileLayout(beforeBoundary, true);
            const intent = deriveIntent(this.#state.peek(), this.options.adapter, this.options.config);
            if (intent)
                this.#reduce(actions.claim(intent));
        }
        this.#driver.sync(this.#state.peek());
        this.#syncTimers();
    }
    #reconcileLayout(before, compensate) {
        this.#view?.flush();
        if (this.#view)
            this.#reduce(actions.measure(this.#view.read()));
        const after = this.#layout();
        const scrollTop = compensate
            ? compensateScroll(before, after)
            : Math.max(0, Math.min(this.#state.peek().measurement.scrollTop, Math.max(0, after.contentHeight - after.viewportHeight)));
        this.#view?.scrollTo(scrollTop);
        this.#reduce(actions.measure({ ...this.#state.peek().measurement, scrollTop }));
    }
    #syncTimers() {
        const state = this.#state.peek();
        const now = this.options.scheduler.now();
        let expiry = null;
        for (const timestamp of state.unmatchedAt.values()) {
            const deadline = timestamp + state.source.retention;
            expiry = expiry === null ? deadline : Math.min(expiry, deadline);
        }
        this.#timer('cleanup', !state.disposed ? expiry : null, () => actions.cleanup(this.options.scheduler.now()));
        this.#timer('live', liveEligible(state, this.options.config)
            ? (this.#timers.get('live')?.at ?? now + this.options.config.liveInterval)
            : null, actions.liveTick);
        this.#timer('lead', leadEligible(state, this.options.adapter)
            ? (this.#timers.get('lead')?.at ?? now + this.options.config.leadInterval)
            : null, actions.leadTick);
    }
    #timer(kind, at, action) {
        const previous = this.#timers.get(kind);
        if (previous?.at === at)
            return;
        previous?.cancel();
        this.#timers.delete(kind);
        if (at !== null) {
            const identity = {};
            const cancel = this.options.scheduler.schedule(Math.max(0, at - this.options.scheduler.now()), () => {
                if (this.#timers.get(kind)?.identity !== identity)
                    return;
                this.#timers.delete(kind);
                this.#dispatch(action());
            });
            this.#timers.set(kind, { at, cancel, identity });
        }
    }
}
//# sourceMappingURL=SymbolTrackedTimeList.js.map